import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
    EmptyPageWrapper,
    StyledImage,
    StyledTypography,
    StyledDescription,
    AddItemsWrapper,
    AddItem,
    AddItemTitle,
    AddItemDescription,
    AddItemButtons,
    AddItemSocialButtonTwitter,
    AddItemSocialButtonDiscord,
    AddItemSocialButtonTiktok,
} from './styles';
import { useAppDispatch } from '@/hooks';
import { addItem, setEditingItemId } from '@/store/actions/yat-page-item-actions';
import { ContentType } from '@/store/types/yat-page-actions';
import { Button } from '@/components/Sections/Profile/Buttons';
import { setNFTManagerPanelOpen } from '@/store/actions/nft-manager';

import { ReactComponent as TwitterIcon } from '@/assets/icons/v2/social/twitter.svg';
import { ReactComponent as DiscordIcon } from '@/assets/icons/v2/social/discord.svg';
import { ReactComponent as TikTokIcon } from '@/assets/icons/v2/social/tiktok.svg';
import { useWalletStore } from '@/store/zustand';

interface EmptyPageProps {
    isCurrentUserProfile?: boolean;
}

export const EmptyPage = ({ isCurrentUserProfile = true }: EmptyPageProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['yat_page', 'onboarding']);

    const wallets = useWalletStore((state) => state.connectedWallets.map((item) => item.walletAddress));

    const isWalletConnected = wallets ? wallets.length > 0 : false;

    const goToWalletConnect = () => {
        navigate('/wallet');
    };

    const handleOpenNFTManager = () => {
        let delay = 0;
        if (!location.pathname.includes('profile/edit')) {
            delay = 500;
            navigate('/profile/edit/');
        }

        setTimeout(() => {
            dispatch(setNFTManagerPanelOpen());
        }, delay);
    };

    const handleCreateNewItem = (url = '') => {
        let delay = 0;
        if (!location.pathname.includes('profile/edit')) {
            delay = 500;
            navigate('/profile/edit/');
        }

        setTimeout(() => {
            const id = uuidv4();
            const layout = {
                isDraggable: false,
                moved: false,
                static: false,
            };
            const newLinkItem = {
                id,
                layout,
                editing: true,
                type: ContentType.LINK,
                order: 0,
                draftOrder: 0,
                categories: [],
                content: {
                    description: '',
                    image: '',
                    title: '',
                    url: url,
                },
                hasPersisted: true,
                collapsed: true,
            };

            dispatch(addItem(newLinkItem));
            dispatch(setEditingItemId(newLinkItem.id));
        }, delay);
    };

    return (
        <EmptyPageWrapper>
            {isCurrentUserProfile && (
                <>
                    <StyledImage src="/assets/img/eyes-emoji.png" />
                    <StyledTypography>{t('no_posts_yet_title')}</StyledTypography>
                    <StyledDescription>{t('no_posts_yet_description')}</StyledDescription>
                    <AddItemsWrapper>
                        <AddItem>
                            <AddItemTitle>{t('profile-add-wallet__title', { ns: 'onboarding' })}</AddItemTitle>
                            <AddItemDescription>
                                {t('profile-add-wallet__description', { ns: 'onboarding' })}
                            </AddItemDescription>
                            <AddItemButtons>
                                {!isWalletConnected ? (
                                    <Button
                                        size="medium"
                                        fluid={false}
                                        onClick={goToWalletConnect}
                                        style={{ width: '100%' }}
                                    >
                                        {t('profile-add-wallet__button', { ns: 'onboarding' })}
                                    </Button>
                                ) : (
                                    <Button
                                        size="medium"
                                        fluid={false}
                                        onClick={handleOpenNFTManager}
                                        style={{ width: '100%' }}
                                    >
                                        {t('profile-add-nft__button', { ns: 'onboarding' })}
                                    </Button>
                                )}
                            </AddItemButtons>
                        </AddItem>
                        <AddItem>
                            <AddItemTitle>{t('profile-add-social__title', { ns: 'onboarding' })}</AddItemTitle>
                            <AddItemDescription>
                                {t('profile-add-social__description', { ns: 'onboarding' })}
                            </AddItemDescription>
                            <AddItemButtons>
                                <AddItemSocialButtonTwitter onClick={() => handleCreateNewItem()}>
                                    <TwitterIcon />
                                </AddItemSocialButtonTwitter>
                                <AddItemSocialButtonDiscord onClick={() => handleCreateNewItem()}>
                                    <DiscordIcon />
                                </AddItemSocialButtonDiscord>
                                <AddItemSocialButtonTiktok onClick={() => handleCreateNewItem()}>
                                    <TikTokIcon />
                                </AddItemSocialButtonTiktok>
                            </AddItemButtons>
                        </AddItem>
                        <AddItem>
                            <AddItemTitle>{t('profile-add-link__title', { ns: 'onboarding' })}</AddItemTitle>
                            <AddItemDescription>
                                {t('profile-add-link__description', { ns: 'onboarding' })}
                            </AddItemDescription>
                            <AddItemButtons>
                                <Button
                                    size="medium"
                                    fluid={false}
                                    onClick={() => handleCreateNewItem()}
                                    style={{ width: '100%' }}
                                >
                                    {t('profile-add-link__button', { ns: 'onboarding' })}
                                </Button>
                            </AddItemButtons>
                        </AddItem>
                    </AddItemsWrapper>
                    {/* Open New Link */}
                </>
            )}
            {!isCurrentUserProfile && (
                <>
                    <StyledImage src="/assets/img/eyes-emoji.png" />
                    <StyledTypography>{t('no_posts_yet')}</StyledTypography>
                </>
            )}
        </EmptyPageWrapper>
    );
};
